class FunnelCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.countdownInterval = null;
    this.swiperInstance = null;
    // Track per-variant auto-add so multiple shipping_protection blocks
    // (e.g. two SP products or concurrent A/B variants) each run exactly
    // once and don't fight over a single boolean flag.
    this.shippingProtectionAutoAdded = new Set();

    // Escape key to close
    this.addEventListener("keyup", (evt) => {
      if (evt.code === "Escape") this.close();
    });

    // Click outside drawer (on overlay / ::before pseudo-element) to close
    this.addEventListener("click", (event) => {
      // Close if click is directly on the host element or the overlay div
      // (not on the drawer inner panel or its children)
      if (event.target === this || event.target.id === "FunnelCartDrawer-Overlay") {
        this.close();
      }
    });

    // Cart icon click interception
    this.setupCartIconInterception();

    // Event delegation for all interactive elements inside the drawer
    this.setupEventDelegation();

    // Make this element discoverable by product-form.js which looks for
    // document.querySelector("cart-drawer"). We register as "cart-drawer"
    // so the existing product-form calls our renderContents() instead of
    // redirecting to /cart.
    this.registerAsCartDrawer();
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  setupCartIconInterception() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  registerAsCartDrawer() {
    // product-form.js looks for document.querySelector("cart-drawer") or
    // document.querySelector("cart-notification"). Since neither exists on
    // funnel pages, it falls back to redirecting to /cart.
    //
    // Strategy: patch product-form instances, intercept querySelector,
    // and intercept fetch to inject sections into /cart/add requests
    // and block premature redirects from third-party scripts.
    const drawer = this;

    // 1. Patch existing product-form instances (multiple attempts to cover timing)
    const patchProductForms = () => {
      document.querySelectorAll("product-form").forEach((pf) => {
        if (!pf.cart || pf.cart.tagName !== "FUNNEL-CART-DRAWER") {
          pf.cart = drawer;
        }
      });
    };
    // product-form.js uses a 100ms setTimeout in connectedCallback
    setTimeout(patchProductForms, 50);
    setTimeout(patchProductForms, 200);
    setTimeout(patchProductForms, 500);

    // 2. Override querySelector so any script looking for "cart-drawer" finds us
    const originalQuerySelector = document.querySelector.bind(document);
    document.querySelector = function (selector) {
      if (selector === "cart-drawer") {
        return drawer;
      }
      return originalQuerySelector(selector);
    };

    // 3. Intercept ALL fetch calls to /cart/add to inject sections
    //    for the Section Rendering API and block premature redirects
    //    from third-party scripts.
    this.interceptCartFetch();
  }

  interceptCartFetch() {
    const drawer = this;
    // Grab whatever fetch exists right now (may already be patched by mt_product-info.liquid)
    const originalFetch = window.fetch.bind(window);
    let cartOperationInProgress = false;

    window.fetch = function (...args) {
      let [url, options] = args;
      const urlStr = String(url || "");

      // Only intercept /cart/add requests
      if (!urlStr.includes("/cart/add")) {
        return originalFetch(...args);
      }

      // Handle JSON body requests — inject sections for Section Rendering API
      if (options && options.body && typeof options.body === "string") {
        try {
          const bodyData = JSON.parse(options.body);

          if (!bodyData.items || !Array.isArray(bodyData.items)) {
            // Inject sections if missing so we get the updated drawer HTML back
            if (!bodyData.sections) {
              bodyData.sections = drawer.getSectionsToRender().map((s) => s.id);
              bodyData.sections_url = window.location.pathname;
            }

            options = Object.assign({}, options, {
              body: JSON.stringify(bodyData),
            });
            args = [url, options];
          }
        } catch (e) {
          // JSON parse failed — pass through
        }
      }

      // Block redirects during the cart operation
      cartOperationInProgress = true;

      return originalFetch(...args)
        .then((response) => {
          if (response.ok) {
            const cloned = response.clone();
            cloned.json().then((parsedState) => {
              if (parsedState.sections) {
                drawer.renderContents(parsedState);
              }
            }).catch(() => {});
          }
          return response;
        })
        .finally(() => {
          setTimeout(() => {
            cartOperationInProgress = false;
          }, 1500);
        });
    };

    // Block window.location redirects during cart operations (from third-party scripts)
    // Our own code sets drawer._allowRedirect = true before intentional redirects
    const originalLocationHref = Object.getOwnPropertyDescriptor(
      window.location.__proto__, "href"
    );
    if (originalLocationHref && originalLocationHref.set) {
      Object.defineProperty(window.location, "href", {
        get: originalLocationHref.get,
        set: function (val) {
          if (drawer._allowRedirect) {
            drawer._allowRedirect = false;
            return originalLocationHref.set.call(this, val);
          }
          if (cartOperationInProgress && (val === "/checkout" || val === "/cart" || val.includes("/checkout") || val.includes("/cart"))) {
            return;
          }
          return originalLocationHref.set.call(this, val);
        },
        configurable: true,
      });
    }
  }

  // Called by product-form.js before submitting
  setActiveElement(element) {
    this.activeElement = element;
  }

  setupEventDelegation() {
    this.addEventListener("click", (event) => {
      const target = event.target;

      // Quantity minus
      const minusBtn = target.closest(".fcart-item__quantity-minus");
      if (minusBtn) {
        event.preventDefault();
        this.handleQuantityChange(minusBtn, -1);
        return;
      }

      // Quantity plus
      const plusBtn = target.closest(".fcart-item__quantity-plus");
      if (plusBtn) {
        event.preventDefault();
        this.handleQuantityChange(plusBtn, 1);
        return;
      }

      // Remove item
      const removeBtn = target.closest(".fcart-item__remove");
      if (removeBtn) {
        event.preventDefault();
        const line = parseInt(removeBtn.dataset.line, 10);
        if (line) this.removeItem(line);
        return;
      }

      // Upsell add
      const upsellBtn = target.closest(".fcart-upsell-add");
      if (upsellBtn) {
        event.preventDefault();
        this.handleUpsellClick(upsellBtn);
        return;
      }

      // Variant picker modal
      const vpClose = target.closest("[data-vp-close]");
      if (vpClose) {
        event.preventDefault();
        this.closeVariantPicker();
        return;
      }

      const vpOption = target.closest("[data-vp-option]");
      if (vpOption) {
        event.preventDefault();
        this.selectVariantPickerOption(vpOption);
        return;
      }

      const vpAdd = target.closest("[data-vp-add]");
      if (vpAdd) {
        event.preventDefault();
        this.confirmVariantPicker();
        return;
      }

      // Club CTA
      const clubCta = target.closest(".fcart-cta--club");
      if (clubCta) {
        event.preventDefault();
        this.handleClubCheckout();
        return;
      }

      // Regular CTA
      const regularCta = target.closest(".fcart-cta--regular");
      if (regularCta) {
        event.preventDefault();
        this.handleRegularCheckout();
        return;
      }
    });

    // Shipping protection toggle (change event).
    // The checkbox carries its own `data-variant-id`, which we trust over
    // any host attribute so multiple SP blocks each toggle their own variant.
    this.addEventListener("change", (event) => {
      const toggle = event.target.closest(".fcart-sp-checkbox");
      if (toggle) {
        this.handleShippingProtectionToggle(toggle);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Open / Close
  // ---------------------------------------------------------------------------

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    // Add animate (enables transitions) then active (makes visible) on next frame
    this.classList.add("animate");
    requestAnimationFrame(() => {
      this.classList.add("active");
    });

    document.body.classList.add("overflow-hidden");

    // Start countdown timer
    this.startCountdownTimer();

    // Initialize Swiper for upsells
    this.initSwiper();

    // Auto-add shipping protection on first open
    this.autoAddShippingProtection();
  }

  close() {
    this.classList.remove("active");
    document.body.classList.remove("overflow-hidden");

    // Clear countdown timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Cart Operations
  // ---------------------------------------------------------------------------

  async addItemToCart(variantId, quantity = 1) {
    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      id: parseInt(variantId, 10),
      quantity: quantity,
      sections: sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body,
      });

      if (response.status === 422) {
        const errorData = await response.json();
        console.error("Error adding item to cart:", errorData.description);
        return;
      }

      const parsedState = await response.json();
      this.renderContents(parsedState);
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  }

  async updateQuantity(line, quantity) {
    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_change_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body,
      });

      const parsedState = await response.json();

      if (parsedState.errors) {
        console.error("Error updating quantity:", parsedState.errors);
        return;
      }

      this.renderContents(parsedState);
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  }

  removeItem(line) {
    this.updateQuantity(line, 0);
  }

  // ---------------------------------------------------------------------------
  // Upsell variant matching
  // ---------------------------------------------------------------------------

  async handleUpsellClick(btn) {
    const card = btn.closest(".fcart-upsell");
    if (!card) return;

    let variants = [];
    let optionNames = [];
    try {
      variants = JSON.parse(card.dataset.variants || "[]");
      optionNames = JSON.parse(card.dataset.optionNames || "[]");
    } catch (_) {
      variants = [];
      optionNames = [];
    }

    const available = variants.filter((v) => v.available);
    if (available.length === 0) return;

    // Single variant → add directly.
    if (variants.length === 1) {
      this.addItemToCart(variants[0].id, 1);
      return;
    }

    // Try to match against cart option values.
    const cartValues = await this.getCartOptionValues();
    const match = this.resolveUpsellVariant(available, cartValues);
    if (match) {
      this.addItemToCart(match.id, 1);
      return;
    }

    // No confident match → open picker.
    this.openVariantPicker({
      productId: card.dataset.productId,
      title: card.dataset.productTitle,
      handle: card.dataset.productHandle,
      image: card.dataset.featuredImage,
      optionNames,
      variants,
    });
  }

  async getCartOptionValues() {
    try {
      const res = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return new Set();
      const cart = await res.json();
      const values = new Set();
      (cart.items || []).forEach((item) => {
        (item.options_with_values || []).forEach((ov) => {
          const norm = this.normalizeOptionValue(ov.value);
          if (norm) values.add(norm);
        });
      });
      return values;
    } catch (_) {
      return new Set();
    }
  }

  normalizeOptionValue(value) {
    if (!value) return "";
    return String(value)
      .toLowerCase()
      .replace(/\bsize\b/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  resolveUpsellVariant(availableVariants, cartValues) {
    if (!cartValues || cartValues.size === 0) return null;

    let bestVariant = null;
    let bestScore = 0;

    availableVariants.forEach((v) => {
      const score = (v.options || []).reduce((acc, optVal) => {
        return cartValues.has(this.normalizeOptionValue(optVal)) ? acc + 1 : acc;
      }, 0);
      if (score > bestScore) {
        bestScore = score;
        bestVariant = v;
      }
    });

    return bestScore > 0 ? bestVariant : null;
  }

  // ---------------------------------------------------------------------------
  // Variant picker modal
  // ---------------------------------------------------------------------------

  openVariantPicker(productData) {
    const modal = this.querySelector(".fcart-vp");
    if (!modal) return;

    this.vpState = {
      productData,
      selection: new Array(productData.optionNames.length).fill(null),
    };

    const titleEl = modal.querySelector(".fcart-vp__title");
    const imgEl = modal.querySelector(".fcart-vp__image");
    const optionsEl = modal.querySelector(".fcart-vp__options");
    const priceEl = modal.querySelector(".fcart-vp__price");
    const addBtn = modal.querySelector("[data-vp-add]");

    if (titleEl) titleEl.textContent = productData.title || "";
    if (imgEl) {
      if (productData.image) {
        imgEl.src = productData.image;
        imgEl.style.display = "";
      } else {
        imgEl.style.display = "none";
      }
    }

    // Build grouped option pickers.
    optionsEl.innerHTML = productData.optionNames
      .map((name, optIndex) => {
        const uniqueValues = [
          ...new Set(
            productData.variants
              .map((v) => (v.options || [])[optIndex])
              .filter((val) => val != null)
          ),
        ];
        const buttons = uniqueValues
          .map((val) => {
            const hasAvailable = productData.variants.some(
              (v) => v.available && (v.options || [])[optIndex] === val
            );
            return `<button
                type="button"
                class="fcart-vp__option-btn"
                data-vp-option="${optIndex}"
                data-vp-value="${this.escapeHtml(val)}"
                ${hasAvailable ? "" : "disabled"}
              >${this.escapeHtml(val)}</button>`;
          })
          .join("");
        return `<div class="fcart-vp__option-group">
            <span class="fcart-vp__option-label">${this.escapeHtml(name)}</span>
            <div class="fcart-vp__option-buttons">${buttons}</div>
          </div>`;
      })
      .join("");

    if (priceEl) priceEl.textContent = "";
    if (addBtn) addBtn.disabled = true;

    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
  }

  selectVariantPickerOption(btn) {
    const modal = this.querySelector(".fcart-vp");
    if (!modal || !this.vpState) return;

    const optIndex = parseInt(btn.dataset.vpOption, 10);
    const value = btn.dataset.vpValue;
    this.vpState.selection[optIndex] = value;

    // Visual state: mark selected within its group.
    const group = btn.closest(".fcart-vp__option-group");
    if (group) {
      group.querySelectorAll(".fcart-vp__option-btn").forEach((b) => {
        b.classList.toggle("is-selected", b === btn);
      });
    }

    this.updateVariantPickerSelection();
  }

  updateVariantPickerSelection() {
    const modal = this.querySelector(".fcart-vp");
    if (!modal || !this.vpState) return;

    const { productData, selection } = this.vpState;
    const addBtn = modal.querySelector("[data-vp-add]");
    const priceEl = modal.querySelector(".fcart-vp__price");

    const allChosen = selection.every((v) => v != null);
    if (!allChosen) {
      if (priceEl) priceEl.textContent = "";
      if (addBtn) addBtn.disabled = true;
      this.vpState.matchedVariant = null;
      return;
    }

    const matched = productData.variants.find((v) =>
      (v.options || []).every((val, i) => val === selection[i])
    );
    this.vpState.matchedVariant = matched || null;

    if (priceEl) {
      priceEl.textContent =
        matched && matched.available
          ? this.formatMoney(matched.price)
          : matched
          ? "Unavailable"
          : "";
    }
    if (addBtn) addBtn.disabled = !matched || !matched.available;
  }

  confirmVariantPicker() {
    if (!this.vpState || !this.vpState.matchedVariant) return;
    const variantId = this.vpState.matchedVariant.id;
    this.closeVariantPicker();
    this.addItemToCart(variantId, 1);
  }

  closeVariantPicker() {
    const modal = this.querySelector(".fcart-vp");
    if (!modal) return;
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    this.vpState = null;
  }

  formatMoney(cents) {
    if (typeof cents !== "number") return "";
    const fmt = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : "USD";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: fmt,
      }).format(cents / 100);
    } catch (_) {
      return "$" + (cents / 100).toFixed(2);
    }
  }

  escapeHtml(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Handle empty cart state
    const itemCount = parsedState.item_count !== undefined
      ? parsedState.item_count
      : (parsedState.items ? parsedState.items.length : null);

    if (itemCount !== null) {
      this.classList.toggle("is-empty", itemCount === 0);
    }

    setTimeout(() => {
      // Re-initialize Swiper after content update
      this.swiperInstance = null;
      this.initSwiper();

      // Open the drawer
      this.open();
    });
  }

  // ---------------------------------------------------------------------------
  // Quantity Handling
  // ---------------------------------------------------------------------------

  handleQuantityChange(button, delta) {
    const line = parseInt(button.dataset.line, 10);
    const quantityElement = button.closest(".fcart-item__quantity");

    if (!line) return;

    let currentQty = 1;
    if (quantityElement) {
      const input = quantityElement.querySelector("input[type='number'], input[name='quantity']");
      if (input) {
        currentQty = parseInt(input.value, 10) || 1;
      } else {
        const span = quantityElement.querySelector(".fcart-item__quantity-input");
        if (span) {
          currentQty = parseInt(span.textContent, 10) || 1;
        }
      }
    }

    const newQty = Math.max(0, currentQty + delta);
    this.updateQuantity(line, newQty);
  }

  // ---------------------------------------------------------------------------
  // Shipping Protection
  // ---------------------------------------------------------------------------

  // Legacy single-variant accessors — kept for any external caller.
  // Internal logic now derives IDs per-toggle / from the CSV attrs so that
  // multiple shipping_protection blocks can coexist.
  get shippingVariantId() {
    return this.getAttribute("data-shipping-variant-id");
  }

  get shippingPreSelected() {
    return this.getAttribute("data-shipping-pre-selected") === "true";
  }

  // Parse ",id1,id2," CSV attributes into an array of trimmed IDs.
  parseCsvAttr(attr) {
    const raw = this.getAttribute(attr) || "";
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  // Resolve the runtime pre-select decision. Allows A/B testing platforms
  // to flip the auto-add behavior by injecting a single line BEFORE the
  // user opens the cart drawer:
  //
  //   window.FCART_SHIPPING_PRE_SELECT = false;  // disable for ALL SP blocks
  //   window.FCART_SHIPPING_PRE_SELECT = true;   // enable for ALL SP blocks
  //   window.FCART_SHIPPING_PRE_SELECT = [12345, 67890];  // explicit variant IDs
  //
  // Anything else (including undefined) falls back to each block's
  // `pre_selected` Liquid setting via `data-shipping-auto-add-ids`.
  //
  // Returns an array of variant ID strings to auto-add (possibly empty).
  resolveShippingAutoAddIds() {
    const override = typeof window !== "undefined"
      ? window.FCART_SHIPPING_PRE_SELECT
      : undefined;

    if (override === false) return [];

    if (Array.isArray(override)) {
      // Explicit list. Keep only the ones that map to a real SP block on
      // this drawer, so a stale or wrong ID can't accidentally add a
      // non-shipping-protection product.
      const known = new Set(this.parseCsvAttr("data-shipping-variant-ids"));
      return override
        .map((id) => String(id))
        .filter((id) => known.has(id));
    }

    if (override === true) {
      // Force-enable: pre-select every SP variant the drawer knows about.
      return this.parseCsvAttr("data-shipping-variant-ids");
    }

    // Default: use the per-block `pre_selected` Liquid setting; fall back
    // to legacy single-variant attrs for older section markup.
    let ids = this.parseCsvAttr("data-shipping-auto-add-ids");
    if (ids.length === 0 && this.shippingPreSelected && this.shippingVariantId) {
      ids = [this.shippingVariantId];
    }
    return ids;
  }

  async autoAddShippingProtection() {
    const autoAddIds = this.resolveShippingAutoAddIds();
    if (autoAddIds.length === 0) return;

    // Drop IDs we've already auto-added this session so re-opens stay idempotent.
    const pending = autoAddIds.filter((id) => !this.shippingProtectionAutoAdded.has(id));
    if (pending.length === 0) return;

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      });
      const cart = await cartResponse.json();
      const inCart = new Set(cart.items.map((item) => String(item.variant_id)));

      for (const id of pending) {
        // Mark before the await so a fast re-open can't queue a duplicate add.
        this.shippingProtectionAutoAdded.add(id);
        if (inCart.has(String(id))) continue;
        await this.addItemToCart(id, 1);
      }
    } catch (error) {
      console.error("Error auto-adding shipping protection:", error);
    }
  }

  async handleShippingProtectionToggle(toggle) {
    // Trust the checkbox's own data-variant-id — each SP block emits its
    // own toggle with its own variant ID, so this is the only way to tell
    // the blocks apart when several are active at once.
    const variantId = toggle && toggle.dataset ? toggle.dataset.variantId : "";
    if (!variantId) return;

    if (toggle.checked) {
      await this.addItemToCart(variantId, 1);
      return;
    }

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      });
      const cart = await cartResponse.json();

      const lineIndex = cart.items.findIndex(
        (item) => String(item.variant_id) === String(variantId)
      );

      if (lineIndex !== -1) {
        // Shopify line items are 1-indexed
        this.removeItem(lineIndex + 1);
      }
    } catch (error) {
      console.error("Error removing shipping protection:", error);
    }
  }

  // ---------------------------------------------------------------------------
  // Club CTA (Membership Checkout)
  // ---------------------------------------------------------------------------

  get membershipVariantId() {
    return this.getAttribute("data-membership-variant-id");
  }

  get membershipSellingPlanId() {
    return this.getAttribute("data-membership-selling-plan-id");
  }

  async handleClubCheckout() {
    if (!this.membershipVariantId) {
      this._allowRedirect = true;
      window.location.href = "/checkout";
      return;
    }

    try {
      // Check if membership is already in cart
      const cartResponse = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      });
      const cart = await cartResponse.json();
      const alreadyInCart = cart.items.some(
        (item) => String(item.variant_id) === String(this.membershipVariantId)
      );

      if (!alreadyInCart) {
        // Build request body with selling plan if available
        const requestBody = {
          id: parseInt(this.membershipVariantId, 10),
          quantity: 1,
        };

        if (this.membershipSellingPlanId) {
          requestBody.selling_plan = parseInt(this.membershipSellingPlanId, 10);
        }

        const response = await fetch(`${routes.cart_add_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error adding membership to cart:", errorData.description);
        }
      }

      this._allowRedirect = true;
      window.location.href = "/checkout";
    } catch (error) {
      console.error("Error adding membership to cart:", error);
      this._allowRedirect = true;
      window.location.href = "/checkout";
    }
  }

  // ---------------------------------------------------------------------------
  // Regular CTA (Non-Club Checkout)
  // ---------------------------------------------------------------------------

  async handleRegularCheckout() {
    if (this.membershipVariantId) {
      try {
        // Check if membership is in cart and remove it
        const cartResponse = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: "application/json" },
        });
        const cart = await cartResponse.json();

        const membershipLineIndex = cart.items.findIndex(
          (item) => String(item.variant_id) === String(this.membershipVariantId)
        );

        if (membershipLineIndex !== -1) {
          // Remove membership item then redirect
          const sections = this.getSectionsToRender().map((section) => section.id);

          await fetch(`${routes.cart_change_url}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              line: membershipLineIndex + 1,
              quantity: 0,
              sections: sections,
              sections_url: window.location.pathname,
            }),
          });
        }
      } catch (error) {
        console.error("Error removing membership from cart:", error);
      }
    }

    this._allowRedirect = true;
    window.location.href = "/checkout";
  }

  // ---------------------------------------------------------------------------
  // Swiper Initialization
  // ---------------------------------------------------------------------------

  initSwiper() {
    if (this.swiperInstance) return;

    const swiperContainer = this.querySelector(".fcart-upsell-swiper");
    if (!swiperContainer) return;

    const slides = swiperContainer.querySelectorAll(".swiper-slide");
    if (slides.length < 2) return;

    // Swiper library is loaded from CDN inside the section; if it hasn't
    // finished loading yet, poll briefly and retry (cheaper than pinning a
    // load event to a CDN script).
    if (typeof Swiper === "undefined") {
      if (!this._swiperRetryCount) this._swiperRetryCount = 0;
      if (this._swiperRetryCount < 20) {
        this._swiperRetryCount += 1;
        setTimeout(() => this.initSwiper(), 100);
      }
      return;
    }

    const pagination = swiperContainer.querySelector(".fcart-upsell-pagination");

    this.swiperInstance = new Swiper(swiperContainer, {
      // `auto` + CSS-sized slides (see .fcart-upsell-section .swiper-slide)
      // produces a consistent peek regardless of drawer width.
      slidesPerView: "auto",
      spaceBetween: 8,
      // Re-measure when the drawer's transform finishes or the container
      // resizes. Fixes the off-by-width issue when initSwiper runs before
      // the open() CSS transition has settled.
      observer: true,
      observeParents: true,
      resizeObserver: true,
      watchOverflow: true,
      pagination: pagination
        ? { el: pagination, clickable: true }
        : false,
    });

    // Belt-and-braces: once the drawer is fully open, force a dimension
    // recalculation in case observer didn't fire for the transform alone.
    const recalc = () => {
      if (this.swiperInstance) this.swiperInstance.update();
    };
    this.addEventListener("transitionend", recalc, { once: true });
    requestAnimationFrame(recalc);
  }

  // ---------------------------------------------------------------------------
  // Countdown Timer
  // ---------------------------------------------------------------------------

  startCountdownTimer() {
    const timerElement = this.querySelector("#fcart-countdown-timer");
    if (!timerElement) return;

    // Clear any existing timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Reset to 10 minutes
    let minutes = 10;
    let seconds = 0;

    const updateDisplay = () => {
      timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    };

    // Initial display
    updateDisplay();

    // Tick every second
    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
          return;
        }
        minutes--;
        seconds = 59;
      } else {
        seconds--;
      }
      updateDisplay();
    }, 1000);
  }

  // ---------------------------------------------------------------------------
  // Section Rendering Helpers
  // ---------------------------------------------------------------------------

  getSectionsToRender() {
    return [
      {
        id: "funnel-cart-drawer",
        selector: "#FunnelCartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }
}

customElements.define("funnel-cart-drawer", FunnelCartDrawer);
